import { apiClient, idempotentConfig, unwrap } from './client';
import type { ApiEnvelope } from './types';

/**
 * API-09: monthly usage per module, read from each product's own meter, with its reconciliation.
 * contracts/proposed/openapi/PROPOSAL-api-09-usage-aggregation.md. Null `totals` means the module has no meter
 * to read, which is not zero: never default it to a number.
 */

export interface UsageLine {
  unit: string;
  dimension: string;
  quantity: number;
  records: number;
}

export interface UsageDifference {
  unit: string;
  dimension: string;
  aggregated: number;
  metered: number;
  difference: number;
}

export type ReconciliationResult = 'MATCHES' | 'DIFFERS' | 'NOT_CAPTURED' | 'NOT_CONNECTED' | 'UNAVAILABLE';

export interface ModuleUsage {
  module: string;
  state: 'CONNECTED' | 'NOT_CONNECTED' | 'UNAVAILABLE';
  reasonCode: string | null;
  reason: string | null;
  source: string | null;
  totals: Record<string, number> | null;
  lines: UsageLine[] | null;
  reconciliation: {
    result: ReconciliationResult;
    snapshotId: string | null;
    capturedAt: string | null;
    capturedBy: string | null;
    differences: UsageDifference[];
  };
  crossCheck: {
    ledger: string;
    result: 'MATCHES' | 'DIFFERS';
    differences: { dimension: string; meter: number; ledger: number; difference: number }[];
  } | null;
}

export interface UsageAggregation {
  hub: string;
  month: string;
  monthOpen: boolean;
  contract: string;
  modules: ModuleUsage[];
}

/** GET /v1/usage/aggregation?month=YYYY-MM */
export async function getUsageAggregation(month?: string): Promise<UsageAggregation> {
  const { data } = await apiClient.get<ApiEnvelope<UsageAggregation>>('/v1/usage/aggregation', {
    params: { month: month || undefined },
  });
  return unwrap(data);
}

/** POST /v1/usage/aggregation/captures */
export async function captureUsage(month: string): Promise<UsageAggregation> {
  const { data } = await apiClient.post<ApiEnvelope<UsageAggregation>>(
    '/v1/usage/aggregation/captures',
    { month },
    idempotentConfig(),
  );
  return unwrap(data);
}
